// NPC traders: ~300 simulated wallets with archetype-driven buy/sell decisions.
// Each tick a random subset makes one decision, keeping cost O(evaluators x few tokens).

import { TUNABLES } from "@degen/shared/config/tunables";
import type { Rng } from "../rng";
import type { GameState } from "../state";
import { playerClout } from "../player";
import { marketBuy, marketSell } from "./market";
import { sentimentBuyMultiplier } from "./sentiment";
import { balanceOf, mcapOf, priceOf, type Token } from "./token";

export type Archetype = keyof typeof TUNABLES.npc.archetypes;

export interface NpcPosition {
  entry: number; // average entry price
  peak: number; // highest price seen while holding
  tick: number; // first buy tick
  hold: number; // planned hold duration in ticks
  target: number; // take-profit multiple (archetype-specific use)
  tookProfit: boolean;
}

export interface NpcTrader {
  id: string;
  archetype: Archetype;
  cash: number;
  riskAppetite: number; // 0..1
  hypeSensitivity: number; // 0..1, higher = buys at lower hype
  // SPEC-NOTE: spec uses Set<accountId>; an array keeps saves JSON-native.
  follows: string[];
  pos: Record<string, NpcPosition>;
}

const N = TUNABLES.npc;

export function createNpcs(rng: Rng): NpcTrader[] {
  const npcs: NpcTrader[] = [];
  for (let i = 0; i < N.count; i++) {
    const archetype = rng.weighted(N.archetypes) as Archetype;
    const cashRange = archetype === "whale" ? N.startCash.whale : N.startCash.default;
    const follows: string[] = [];
    if (archetype === "copy") {
      const n = rng.int(N.followsPerCopytrader[0], N.followsPerCopytrader[1]);
      for (let j = 0; j < n; j++) follows.push(`acct:${rng.int(0, N.accounts - 1)}`);
    }
    npcs.push({
      id: `n${i}`,
      archetype,
      cash: Math.round(rng.range(cashRange[0], cashRange[1])),
      riskAppetite: rng.next(),
      hypeSensitivity: rng.next(),
      follows,
      pos: {},
    });
  }
  return npcs;
}

export function npcDailyStipend(npcs: NpcTrader[], rng: Rng) {
  for (const n of npcs) {
    const whale = n.archetype === "whale";
    const r = whale ? N.dailyStipend.whale : N.dailyStipend.default;
    const cap = whale ? N.maxCash.whale : N.maxCash.default;
    n.cash = Math.min(cap, n.cash + rng.range(r[0], r[1]));
  }
}

interface Ctx {
  state: GameState;
  rng: Rng;
  live: Token[];
  tick: number;
  buyMult: number;
}

export function runNpcTraders(state: GameState, rng: Rng, live: Token[]) {
  if (!live.length) return;
  const ctx: Ctx = { state, rng, live, tick: state.tick, buyMult: sentimentBuyMultiplier(state.market.sentiment) };
  const evals = Math.round(state.npcs.length * N.evalFraction);
  for (let i = 0; i < evals; i++) {
    const npc = state.npcs[rng.int(0, state.npcs.length - 1)];
    if (npc.archetype === "whale" && !rng.chance(N.whale.evalScale)) continue;
    if (npc.archetype === "bot") botAct(npc, ctx);
    else if (!trySell(npc, ctx)) tryBuy(npc, ctx);
  }
}

// ---------- helpers ----------

function sizeUsd(npc: NpcTrader, range: readonly [number, number], rng: Rng): number {
  return npc.cash * rng.range(range[0], range[1]) * (0.5 + npc.riskAppetite);
}

function buy(npc: NpcTrader, t: Token, usd: number, ctx: Ctx, hold: number, target = 0) {
  usd = Math.min(usd, npc.cash);
  if (usd < N.minTradeUsd) return false;
  const before = balanceOf(t, npc.id);
  const r = marketBuy(ctx.state, t, npc.id, usd);
  if (!r.ok) return false;
  npc.cash -= r.usd;
  const paid = r.usd / r.tokens;
  const p = npc.pos[t.id];
  if (p) {
    p.entry = (before * p.entry + r.tokens * paid) / (before + r.tokens);
  } else {
    npc.pos[t.id] = { entry: paid, peak: priceOf(t), tick: ctx.tick, hold, target, tookProfit: false };
  }
  return true;
}

function sell(npc: NpcTrader, t: Token, fraction: number, ctx: Ctx) {
  const bal = balanceOf(t, npc.id);
  const r = marketSell(ctx.state, t, npc.id, fraction >= 0.999 ? bal : bal * fraction);
  if (r.ok) npc.cash += r.usd;
  if (balanceOf(t, npc.id) <= 0) delete npc.pos[t.id];
}

function positionCount(npc: NpcTrader) {
  let n = 0;
  for (const _ in npc.pos) n++;
  return n;
}

/** Pick from candidates weighted by `w`; returns undefined if nothing qualifies. */
const scratch: number[] = []; // reused to avoid per-decision allocations (GC spikes on phones)
function pickWeighted(ctx: Ctx, w: (t: Token) => number): Token | undefined {
  scratch.length = ctx.live.length;
  for (let i = 0; i < ctx.live.length; i++) scratch[i] = w(ctx.live[i]);
  const i = ctx.rng.weightedIndex(scratch);
  return i < 0 ? undefined : ctx.live[i];
}

/** Hype gates conviction: a chosen token is only bought with probability ~ its hype. */
function hypeGate(t: Token, ctx: Ctx): boolean {
  return ctx.rng.chance(t.hype / 100);
}

// ---------- sells ----------

/** Returns true if the NPC used its decision on a sell. */
function trySell(npc: NpcTrader, ctx: Ctx): boolean {
  const { rng, tick } = ctx;
  for (const id in npc.pos) {
    const t = ctx.state.market.tokens[id];
    if (!t || t.phase === "dead" || balanceOf(t, npc.id) <= 0) {
      delete npc.pos[id];
      continue;
    }
    const pos = npc.pos[id];
    const price = priceOf(t);
    if (price > pos.peak) pos.peak = price;
    const pnl = price / pos.entry - 1; // e.g. 1.0 = +100%
    const held = tick - pos.tick;

    let frac = 0;
    const fud = recentPlayerFud(ctx, t.id);
    if (t.rugged && npc.archetype !== "diamond" && rng.chance(0.8)) frac = 1;
    else if (fud > 0 && npc.archetype !== "diamond" && npc.archetype !== "whale" && rng.chance(fud)) frac = 1;
    else {
      switch (npc.archetype) {
        case "sniper":
          if (pnl + 1 >= pos.target || held > N.sniper.maxHoldTicks) frac = 1;
          break;
        case "degen":
          if (held > pos.hold) frac = 1;
          else if (pnl <= -N.degen.panicDrawdown && rng.chance(0.6)) frac = 1;
          else if (!pos.tookProfit && pnl + 1 >= N.degen.takeProfit) frac = 0.5;
          break;
        case "paper":
          if (price < pos.peak * (1 - N.paper.stopFromPeak) || held > pos.hold) frac = 1;
          break;
        case "diamond":
          if (!pos.tookProfit && pnl + 1 >= N.diamond.takeProfit) frac = 0.3;
          else if (t.hype < N.diamond.deadHype && held > TUNABLES.clock.ticksPerDay && rng.chance(0.05)) frac = 1;
          break;
        case "whale":
          if (pnl + 1 >= N.whale.takeProfit || pnl <= -N.whale.stopLoss || held > pos.hold) frac = 1;
          break;
        case "copy":
          if (held > pos.hold) frac = 1;
          else if (pnl <= -N.copy.panicDrawdown && rng.chance(0.6)) frac = 1;
          else if (!pos.tookProfit && pnl + 1 >= N.copy.takeProfit) frac = 0.5;
          break;
        case "farmer":
          if (held > pos.hold) frac = 1;
          break;
      }
    }
    if (frac > 0) {
      if (frac < 1) pos.tookProfit = true;
      sell(npc, t, frac, ctx);
      return true;
    }
  }
  return false;
}

// ---------- buys ----------

function tryBuy(npc: NpcTrader, ctx: Ctx) {
  const { rng, tick, buyMult } = ctx;
  if (npc.cash < N.minTradeUsd) return;
  const full = positionCount(npc) >= N.maxPositions;
  const age = (t: Token) => tick - t.bornTick;
  const usable = (t: Token) => !t.rugged && !(full && !npc.pos[t.id]);

  switch (npc.archetype) {
    case "sniper": {
      if (!rng.chance(N.sniper.buyChance * buyMult)) return;
      const t = pickWeighted(ctx, (t) => (usable(t) && age(t) <= N.sniper.maxTokenAge && !npc.pos[t.id] ? 1 : 0));
      if (t) buy(npc, t, sizeUsd(npc, N.sniper.size, rng), ctx, 0, rng.range(N.sniper.takeProfit[0], N.sniper.takeProfit[1]));
      return;
    }
    case "degen": {
      if (!rng.chance(N.degen.buyChance * buyMult)) return;
      const th = N.degen.hypeThreshold;
      const threshold = th[1] - (th[1] - th[0]) * npc.hypeSensitivity;
      const t = pickWeighted(ctx, (t) => (usable(t) && t.hype > threshold ? t.hype * t.hype : 0));
      if (t && hypeGate(t, ctx)) buy(npc, t, sizeUsd(npc, N.degen.size, rng), ctx, rng.int(N.degen.holdTicks[0], N.degen.holdTicks[1]));
      return;
    }
    case "paper": {
      if (!rng.chance(N.paper.buyChance * buyMult)) return;
      const t = pickWeighted(ctx, (t) => (usable(t) && t.hype > N.paper.minHype ? t.hype : 0));
      if (t && hypeGate(t, ctx)) buy(npc, t, sizeUsd(npc, N.paper.size, rng), ctx, rng.int(N.paper.holdTicks[0], N.paper.holdTicks[1]));
      return;
    }
    case "diamond": {
      if (!rng.chance(N.diamond.buyChance * buyMult)) return;
      const t = pickWeighted(ctx, (t) => {
        if (!usable(t) || age(t) < N.diamond.minAgeTicks || t.hype < N.diamond.minHype) return 0;
        const p = npc.pos[t.id];
        // Adds on dips to tokens it already holds.
        if (p) return priceOf(t) < p.entry * (1 - N.diamond.dipAdd) ? 3 : 0;
        return t.hype;
      });
      if (t && (npc.pos[t.id] || hypeGate(t, ctx))) buy(npc, t, sizeUsd(npc, N.diamond.size, rng), ctx, Infinity);
      return;
    }
    case "whale": {
      if (!rng.chance(N.whale.buyChance * buyMult)) return;
      const t = pickWeighted(ctx, (t) => (usable(t) && mcapOf(t) >= N.whale.minMcap ? t.hype * Math.sqrt(mcapOf(t)) : 0));
      if (t && hypeGate(t, ctx)) buy(npc, t, sizeUsd(npc, N.whale.size, rng), ctx, rng.int(N.whale.holdTicks[0], N.whale.holdTicks[1]));
      return;
    }
    case "copy": {
      const shill = latestFollowedShill(npc, ctx);
      if (!shill) return;
      const t = ctx.state.market.tokens[shill.tokenId];
      if (!t || t.phase === "dead" || !usable(t) || npc.pos[t.id]) return;
      if (!rng.chance(N.copy.reactChance * shill.strength * buyMult)) return;
      buy(npc, t, sizeUsd(npc, N.copy.size, rng), ctx, rng.int(N.copy.holdTicks[0], N.copy.holdTicks[1]));
      return;
    }
    case "farmer": {
      // SPEC-NOTE: farmers mostly farm airdrops (M6). Until then they trade rarely and small.
      if (!rng.chance(N.farmer.buyChance * buyMult)) return;
      const t = pickWeighted(ctx, (t) => (usable(t) ? t.hype : 0));
      if (t && hypeGate(t, ctx)) buy(npc, t, sizeUsd(npc, N.farmer.size, rng), ctx, rng.int(N.farmer.holdTicks[0], N.farmer.holdTicks[1]));
      return;
    }
  }
}

/** Chance a holder dumps because the player FUDed this token recently (0 if no FUD). */
function recentPlayerFud(ctx: Ctx, tokenId: string): number {
  const shills = ctx.state.market.shills;
  for (let i = shills.length - 1; i >= 0; i--) {
    const s = shills[i];
    if (s.bear && s.by === "player" && s.tokenId === tokenId) {
      const clout = playerClout(ctx.state.player);
      return TUNABLES.social.call.fudSellChance * s.strength * (clout / (clout + 5_000));
    }
  }
  return 0;
}

/** Most recent shill by an account this copytrader follows (the player counts, scaled by clout). */
function latestFollowedShill(npc: NpcTrader, ctx: Ctx) {
  const shills = ctx.state.market.shills;
  for (let i = shills.length - 1; i >= 0; i--) {
    const s = shills[i];
    if (s.bear) continue;
    if (s.by === "player") {
      const clout = playerClout(ctx.state.player);
      // Reach: tiny accounts barely move copytraders, big trusted ones move most of them.
      const reach = clout / (clout + 5_000);
      return { ...s, strength: s.strength * reach };
    }
    if (npc.follows.includes(s.by)) return s;
  }
  return null;
}

function botAct(npc: NpcTrader, ctx: Ctx) {
  const { rng } = ctx;
  if (!rng.chance(N.bot.actChance)) return;
  const t = rng.pick(ctx.live);
  if (t.rugged) return;
  if (balanceOf(t, npc.id) > 0 && rng.chance(0.5)) sell(npc, t, rng.range(0.3, 1), ctx);
  else buy(npc, t, rng.range(N.bot.usd[0], N.bot.usd[1]), ctx, 0);
}
